import { useCallback, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { ChangeUserDebtDetailsParams, useChangeUserDebtDetailsMutation } from 'entities/User';
import { transformEmptyStringToNull } from 'shared/utils/helpers/transformEmptyStringToNull';

interface HookArgs {
  userId: string | undefined;
  updatedData: Nullable<ChangeUserDebtDetailsParams>;
  onSuccess?: () => void;
}

interface HookApi {
  isOpenedConfirm: boolean;
  isLoading: boolean;
  handleChangeUserDetails: () => void;
  openConfirmModal: () => void;
  closeConfirmModal: () => void;
}

export const useChangeUserDebtDetails = ({ userId, updatedData, onSuccess }: HookArgs): HookApi => {
  const [isOpenedConfirm, setOpenedConfirm] = useState(false);

  const { t } = useAppTranslation('common');

  const [changeUserDebtDetails, { isLoading }] = useChangeUserDebtDetailsMutation();

  const openConfirmModal = useCallback((): void => {
    setOpenedConfirm(true);
  }, []);

  const closeConfirmModal = useCallback((): void => {
    setOpenedConfirm(false);
  }, []);

  const handleChangeUserDetails = useCallback(async (): Promise<void> => {
    if (userId && updatedData) {
      try {
        const params = transformEmptyStringToNull(updatedData);

        if (updatedData.disposalContentStatus === undefined) {
          params.disposalContentStatus = null;
        }

        await changeUserDebtDetails({
          userId,
          params,
        }).unwrap();
        showNotification('info', t('Success'), t('User has been successfully updated'));
        onSuccess?.();
      } catch (error) {
        console.log(error);
        showNotification('error', t('Error'), t('Error when updating user'));
      } finally {
        closeConfirmModal();
      }
    }
  }, [userId, updatedData, changeUserDebtDetails, t, onSuccess, closeConfirmModal]);

  return {
    openConfirmModal,
    closeConfirmModal,
    handleChangeUserDetails,
    isOpenedConfirm,
    isLoading,
  };
};
